/**
 * Daily Rewards Component
 * Login streak tracking with bonus VibeCoins
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { X, Gift, Flame, Star, Trophy } from 'lucide-react';

const STREAK_REWARDS = [
  { day: 1, coins: 25, bonus: null },
  { day: 2, coins: 30, bonus: null },
  { day: 3, coins: 40, bonus: 'Glow Effect' },
  { day: 4, coins: 50, bonus: null },
  { day: 5, coins: 75, bonus: null },
  { day: 6, coins: 100, bonus: 'Sparkle Effect' },
  { day: 7, coins: 200, bonus: 'VIP Badge' },
];

const DailyRewardsModal = ({ 
  isOpen, 
  onClose, 
  currentStreak = 0, 
  lastClaimDate,
  canClaim,
  onClaim 
}) => {
  if (!isOpen) return null;

  const todayReward = STREAK_REWARDS[Math.min(currentStreak, 6)];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-md bg-gradient-to-b from-yellow-900/30 via-gray-900 to-gray-950 rounded-2xl border border-yellow-500/30 shadow-2xl overflow-hidden"
      >
        {/* Header with flame animation */}
        <div className="p-6 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-yellow-600/20 to-transparent" />
          <motion.div
            animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="relative inline-block"
          >
            <Gift className="w-16 h-16 text-yellow-400 mx-auto mb-2" />
          </motion.div>
          <h2 className="text-2xl font-bold text-white">Daily Rewards</h2>
          <div className="flex items-center justify-center gap-2 mt-2">
            <Flame className="w-5 h-5 text-orange-500" />
            <span className="text-orange-400 font-bold text-lg">{currentStreak} Day Streak!</span>
            <Flame className="w-5 h-5 text-orange-500" />
          </div>
          
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Streak Calendar */}
        <div className="px-4 pb-4">
          <div className="grid grid-cols-7 gap-2">
            {STREAK_REWARDS.map((reward, index) => {
              const isCompleted = index < currentStreak;
              const isCurrent = index === currentStreak && canClaim;
              const isLocked = index > currentStreak;
              
              return (
                <motion.div
                  key={reward.day}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`relative p-2 rounded-xl text-center ${
                    isCompleted
                      ? 'bg-gradient-to-br from-green-600/30 to-emerald-600/30 border border-green-500/50'
                      : isCurrent
                      ? 'bg-gradient-to-br from-yellow-600/30 to-orange-600/30 border-2 border-yellow-500 animate-pulse'
                      : 'bg-black/30 border border-white/10'
                  }`}
                >
                  <p className="text-white/60 text-xs mb-1">Day {reward.day}</p>
                  <p className={`font-bold text-sm ${isCompleted ? 'text-green-400' : isCurrent ? 'text-yellow-400' : 'text-white/40'}`}>
                    +{reward.coins}
                  </p>
                  {isCompleted && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                  {reward.bonus && (
                    <div className="mt-1">
                      <Star className="w-3 h-3 text-yellow-500 mx-auto" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Today's Reward */}
        <div className="mx-4 mb-4 p-4 bg-gradient-to-r from-yellow-600/20 to-orange-600/20 rounded-xl border border-yellow-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-xs">Today's Reward</p>
              <p className="text-yellow-400 font-bold text-2xl">+{todayReward.coins} 💰</p>
              {todayReward.bonus && (
                <p className="text-purple-400 text-sm flex items-center gap-1">
                  <Star className="w-4 h-4" />
                  Bonus: {todayReward.bonus}
                </p>
              )}
            </div>
            <Button
              onClick={onClaim}
              disabled={!canClaim}
              className={`${
                canClaim
                  ? 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 animate-pulse'
                  : 'bg-gray-700 cursor-not-allowed'
              }`}
            >
              {canClaim ? 'Claim!' : 'Claimed ✓'}
            </Button>
          </div>
        </div>

        {/* 7-Day Bonus */}
        <div className="mx-4 mb-4 p-3 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-xl border border-purple-500/30">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-white font-medium">7-Day Streak Bonus</p>
              <p className="text-white/60 text-xs">Complete 7 days for 200 coins + VIP Badge!</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-yellow-500/20 bg-black/30">
          <p className="text-center text-white/40 text-xs">
            Come back tomorrow to keep your streak going!
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default DailyRewardsModal;
